import React, { ReactNode } from 'react'
import { CartesianGrid, Dot, Line, LineChart, Tooltip } from 'recharts'
import Button from 'antd/lib/button'

import { orderPackageAgain } from '~/firebase'
import { Currencies, CurrencyObject, PurchasedProduct, YieldValue } from '~/types'
import { SNAPSHOT_INTERVAL } from '~/utils/coinApi'

import './style.css'

const MHS = 1000000


interface PropTypes {
  item: PurchasedProduct
  yieldValues: YieldValue[]
}

type CYieldValue = YieldValue & {cumulativeYield: CurrencyObject<number>}


export default class Worker extends React.PureComponent<PropTypes> {
  state = {selectedUnit: 'ETH' as Currencies}
  colors: CurrencyObject<string> = {
    CZK: 'gold',
    ETH: '#8884d8',
    EUR: 'silver',
    USD: 'brown',
  }

  scrollContainerRef

  renderTooltip = ({content, ...props}): ReactNode => { // eslint-disable-line no-unused-vars
    if (!props.payload.length) return null

    const datePurchased = new Date(this.props.item.purchaseTimestamp)
    const dateSnapshot = new Date(
      datePurchased.getFullYear(),
      datePurchased.getMonth(),
      datePurchased.getDate() + props.label + 1
    )

    props.label = dateSnapshot.toLocaleDateString()
    props.coordinate.x -= this.scrollContainerRef.scrollLeft
    props.payload = Object.keys(this.colors).map((key: Currencies) => {
      const payload: CYieldValue = props.payload[0].payload
      return {
        payload,
        color: this.colors[key],
        name: key,
        value: (payload.cumulativeYield[key] * this.props.item.computingPower * MHS).toFixed(key === 'ETH' ? 3 : 0),
      }
    })
    return <Tooltip {...props}/>
  }

  setRefAndHeight = (el: HTMLDivElement) => {
    this.scrollContainerRef = el
    if (el) el.style.height = `calc(100% + ${el.offsetHeight - el.clientHeight - 3}px)`
  }

  onPurchase = ev => {
    const key = ev.currentTarget.value
    orderPackageAgain(key)
  }

  render() {
    const {selectedUnit} = this.state
    const yieldValues = this.props.yieldValues as YieldValue[]
    const {
      computingPower,
      description,
      duration,
      expirationTimestamp,
      name,
      price,
      productId,
      purchaseTimestamp,
    } = this.props.item

    const datePurchased = new Date(purchaseTimestamp)
    const date = datePurchased.toLocaleDateString(undefined, {day: 'numeric', month: 'short'})
    const year = datePurchased.getFullYear()
    const dateExpires = new Date(expirationTimestamp)


    const firstIndex = yieldValues.findIndex(el => el.timestamp > purchaseTimestamp)
    const maybeLastIndex  = yieldValues.findIndex(el => el.timestamp > expirationTimestamp)
    const lastIndex = maybeLastIndex !== -1 ? maybeLastIndex : undefined

    const accumulator: CurrencyObject<number> = { CZK: 0, ETH: 0, EUR: 0, USD: 0 }
    const yieldGraph: CYieldValue[] = yieldValues.slice(firstIndex, lastIndex).map(item => {
      const newestEthYield = item.ethphs * SNAPSHOT_INTERVAL

      return {
        ...item,
        cumulativeYield: {
          CZK: accumulator.CZK += newestEthYield * item.CZK,
          ETH: accumulator.ETH += newestEthYield,
          EUR: accumulator.EUR += newestEthYield * item.EUR,
          USD: accumulator.USD += newestEthYield * item.USD,
        },
      }
    })

    const totalYield = (yieldGraph[yieldGraph.length - 1].cumulativeYield[selectedUnit] * computingPower * MHS)
    const formattedTotalYield = totalYield > 9999
      ? `${(totalYield / 1000).toFixed(1)}k`
      : totalYield.toFixed(selectedUnit === 'ETH' ? 3 : 0)

    const minWidth = window.innerWidth > 1200 ? window.innerWidth - 300 : 900
    const columnWidth = yieldGraph.length * 20 <= minWidth ? minWidth / yieldGraph.length : 20
    const width = yieldGraph.length * columnWidth

    const RenderGraph = () => (
      <LineChart data={yieldGraph} className="styleOverride"
        width={width} style={{minWidth}} height={140} /* also set height in css */
        margin={{top: 2, right: 2, left: 2, bottom: 10}}
      >
        <CartesianGrid strokeDasharray="3 3"/>
        <Tooltip content={this.renderTooltip}/>
        <Line
          type="monotone" stroke={this.colors[selectedUnit]} dot={{r: 2}}
          dataKey={(x: CYieldValue) => x.cumulativeYield[selectedUnit] * computingPower * MHS}
          name={selectedUnit}
          activeDot={p => <Dot {...p} r={4}/>}/>
      </LineChart>
    )

    return ( /* eslint-disable max-len */
      <div className="ant-col-xs-24 ant-col-sm-24 ant-col-md-24 ant-col-lg-24" style={{marginBottom: 16}}>
        <div className="purchaseCard isoWidgetsWrapper sc-jzJRlG bAoCRe">

          <article
            className="isoStickerWidget sc-jKJlTe gbcuIV"
            style={{backgroundColor: `rgb(228, 228, ${255 - computingPower / 2})`}}
          >
            <div className="isoBlockWrapper dates">
              <time className="datePurchased" title="Purchase date">
                <div>{date}</div>
                <div>{year}</div>
              </time>
              <time className="dateExpires" title={`Expiration date (duration ${duration} months)`}>
                <div>{dateExpires.toLocaleDateString()}</div>
              </time>
            </div>
            <div className="isoBlockWrapper hashrate" title="Computing power">
              <div className="hashrate">{computingPower}</div>
              <div className="hashunitWrapper">
                <div className="hashunit">MH/s</div>
              </div>
            </div>
            <div className="isoContentWrapper graph">
              <div className="absoluteContainer">
                <div className="overflowContainer">
                  <div className="scrollContainer" ref={this.setRefAndHeight}>
                    <RenderGraph/>
                  </div>
                </div>
              </div>
              <div className="description"><span className="isoStatNumber">{name}</span><span>{description}</span></div>
              <div className="graphSwitcher">
                <Button size="small" type="ghost" ghost={selectedUnit === 'ETH'} title="ETH" onClick={() => this.setState({selectedUnit: 'ETH'})}>Ξ</Button>
                <Button size="small" type="ghost" ghost={selectedUnit === 'EUR'} title="EUR" onClick={() => this.setState({selectedUnit: 'EUR'})}>€</Button>
                <Button size="small" type="ghost" ghost={selectedUnit === 'USD'} title="USD" onClick={() => this.setState({selectedUnit: 'USD'})}>$</Button>
                <Button size="small" type="ghost" ghost={selectedUnit === 'CZK'} title="CZK" onClick={() => this.setState({selectedUnit: 'CZK'})}>,-</Button>
              </div>
            </div>
            <div className="isoBlockWrapper hashrate yield" title={`Amount mined: ${totalYield.toFixed(selectedUnit === 'ETH' ? 5 : 2)} ${selectedUnit}`}>
              <div className="hashrate">{formattedTotalYield}</div>
              <div className="hashunitWrapper">
                <div className="hashunit">{selectedUnit}</div>
              </div>
            </div>
            <button className="isoCartWrapper" title={`Buy again for ${price} €`} value={productId} onClick={this.onPurchase}>
              <i className="ion-android-cart"/>
            </button>
          </article>
        </div>
      </div>
    )
  }
}

// rgb(114, 102, 186)
// rgb(66, 165, 246)
// rgb(126, 211, 32)
// rgb(247, 93, 129)



// WEBPACK FOOTER //
// ./src/components/Worker/index.tsx